/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import Textfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';

const i18n = defineMessages({
	placeholder: {
		id: 'ai-smart-button.ai-smart-button.ai-smart-button-ui.quick-input.placeholder',
		defaultMessage: 'Ask Rovo anything…',
		description: 'Placeholder for the quick question input in the expanded Rovo button',
	},
	submitLabel: {
		id: 'ai-smart-button.ai-smart-button.ai-smart-button-ui.quick-input.submitLabel',
		defaultMessage: 'Send question',
		description: 'Accessible label for the button that submits a quick question to Rovo',
	},
});

const styles = cssMap({
	form: {
		display: 'flex',
		alignItems: 'center',
		width: '320px',
		gap: token('space.100'),
		paddingInlineStart: token('space.150'),
	},
	input: {
		color: token('color.text.inverse'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- styles the native placeholder exposed by Textfield
		'&::placeholder': {
			color: token('color.text.subtlest'),
		},
	},
	submitButtonWrapper: {
		display: 'flex',
		borderRadius: token('radius.full'),
	},
	disabledSubmitButtonWrapper: {
		backgroundColor: token('color.background.neutral.bold.hovered'),
	},
});

export function QuickInput({
	onSubmit,
}: {
	onSubmit?: (prompt: string) => void;
}): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [prompt, setPrompt] = useState('');
	const trimmedPrompt = prompt.trim();

	return (
		<form
			css={styles.form}
			onSubmit={(event) => {
				event.preventDefault();
				if (!trimmedPrompt) {
					return;
				}
				onSubmit?.(trimmedPrompt);
			}}
		>
			<Textfield
				appearance="none"
				aria-label={formatMessage(i18n.placeholder)}
				placeholder={formatMessage(i18n.placeholder)}
				testId="rovo-button-quick-input"
				value={prompt}
				onChange={(event) => setPrompt(event.currentTarget.value)}
				// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides -- custom text and placeholder colors are required inside the button's dark pill surface
				css={styles.input}
			/>
			<span
				css={[styles.submitButtonWrapper, !trimmedPrompt && styles.disabledSubmitButtonWrapper]}
				data-testid="rovo-button-quick-input-submit-wrapper"
			>
				<IconButton
					appearance="primary"
					icon={(iconProps) => (
						<ArrowRightIcon
							{...iconProps}
							color={!trimmedPrompt ? token('color.icon.subtle') : undefined}
						/>
					)}
					isDisabled={!trimmedPrompt}
					label={formatMessage(i18n.submitLabel)}
					shape="circle"
					type="submit"
				/>
			</span>
		</form>
	);
}
